/**
 * MapMarkerIcon — pure helper that produces Leaflet divIcon HTML
 * for inventory item markers and cluster bubbles. Mirrors the
 * Apple-Photos-style thumbnail markers.
 *
 * Each marker is a 56×56 image with a colored border + an 8px
 * downward triangle tail pointing to the exact coord. Cluster
 * bubbles reuse the same shape with a count badge in the bottom-
 * right corner.
 *
 * Border colors communicate custody state at a glance:
 *   - Owned (default)           → white 2px
 *   - Lent out (you own)        → orange 3px + ↗ overlay
 *   - Borrowed (you hold)       → purple 3px + ↙ overlay
 *   - Overdue (past due_date)   → red 3px + "!" badge
 *
 * Web-only — relies on raw HTML strings consumed by Leaflet's
 * L.divIcon. The native fallback path renders nothing.
 */

import type { EnhancedInventoryItem, InventoryItemResponse } from '../../../api/types';
import { fpColors } from '../../../styles/theme';

export type AnyItem = EnhancedInventoryItem | InventoryItemResponse;

export type MarkerState =
  | 'owned'
  | 'lent-out'
  | 'borrowed'
  | 'overdue';

const SIZE = 56;
const TAIL = 8;

const BORDER_COLOR: Record<MarkerState, string> = {
  owned: '#FFFFFF',
  'lent-out': fpColors.tertiary, // orange
  borrowed: fpColors.primary, // purple
  overdue: fpColors.error, // red
};

const BORDER_WIDTH: Record<MarkerState, number> = {
  owned: 2,
  'lent-out': 3,
  borrowed: 3,
  overdue: 3,
};

const OVERLAY_GLYPH: Record<MarkerState, string | null> = {
  owned: null,
  'lent-out': '↗', // lent out (going out)
  borrowed: '↙', // borrowed (coming in)
  overdue: '!',
};

/**
 * Derive the marker state for an item given the viewing user.
 * Falls back to 'owned' for any item the viewer doesn't have
 * the context to classify.
 */
export function deriveMarkerState(
  item: AnyItem,
  currentUserId: number | null | undefined,
): MarkerState {
  const owner = (item as any).user_id ?? (item as any).owner_user_id;
  const custodian = (item as any).current_custodian_user_id;
  const due = (item as any).custody_due_date;

  const overdue =
    due && new Date(due).getTime() < Date.now() && custodian != null && custodian !== owner;
  if (overdue) return 'overdue';

  if (!currentUserId) return 'owned';
  // viewer is the owner but a different user holds it
  if (owner === currentUserId && custodian && custodian !== owner) return 'lent-out';
  // viewer is the holder but doesn't own
  if (custodian === currentUserId && owner !== currentUserId) return 'borrowed';
  return 'owned';
}

const fallbackImage = (item: AnyItem): string => {
  const photo =
    (item as any).primary_photo_url ?? (item as any).source_image_url;
  if (photo) return photo;
  // Inline SVG fallback when the item has no image at all — a
  // primary-color square with an inventory glyph.
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 56 56'>
    <rect width='56' height='56' fill='${fpColors.primaryContainer}'/>
    <text x='28' y='36' font-size='28' text-anchor='middle' fill='${fpColors.primary}'>📦</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Build the HTML used by L.divIcon for a single-item marker.
 */
export function buildMarkerHTML(
  item: AnyItem,
  state: MarkerState,
): string {
  const border = BORDER_COLOR[state];
  const borderW = BORDER_WIDTH[state];
  const overlay = OVERLAY_GLYPH[state];
  const photo = escapeHtml(fallbackImage(item));
  const name = escapeHtml(String(item.item_name ?? 'Item'));

  const overlayHTML = overlay
    ? `<span class="founsi-marker-overlay founsi-marker-overlay-${state}">${overlay}</span>`
    : '';

  return `
    <div class="founsi-marker founsi-marker-${state}" style="--border:${border};--border-w:${borderW}px">
      <img src="${photo}" alt="${name}" loading="lazy" />
      ${overlayHTML}
      <span class="founsi-marker-tail" style="border-top-color:${border}"></span>
    </div>
  `;
}

/**
 * Build the HTML used by L.divIcon for a cluster bubble. Uses
 * the first item's thumbnail with a count badge bottom-right.
 */
export function buildClusterHTML(items: AnyItem[]): string {
  const count = items.length;
  const first = items[0];
  const photo = first ? escapeHtml(fallbackImage(first)) : '';
  const border = BORDER_COLOR.owned;
  const borderW = BORDER_WIDTH.owned;
  return `
    <div class="founsi-marker founsi-marker-cluster" style="--border:${border};--border-w:${borderW}px">
      <img src="${photo}" alt="${count} items" loading="lazy" />
      <span class="founsi-marker-count">${count}</span>
      <span class="founsi-marker-tail" style="border-top-color:${border}"></span>
    </div>
  `;
}

/**
 * Global CSS injected once on first import. Leaflet renders the
 * divIcon HTML into a `.leaflet-marker-icon` wrapper, so we use a
 * specific selector to size/position our content correctly.
 */
let stylesInjected = false;
export function ensureMarkerStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'founsi-marker-styles';
  style.innerHTML = `
    .founsi-marker {
      position: relative;
      width: ${SIZE}px;
      height: ${SIZE}px;
      border-radius: 10px;
      overflow: visible;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
      background: ${fpColors.surfaceContainerLow};
      border: var(--border-w) solid var(--border);
      box-sizing: border-box;
    }
    .founsi-marker img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 8px;
      display: block;
    }
    .founsi-marker-tail {
      position: absolute;
      bottom: -${TAIL}px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: ${TAIL}px solid transparent;
      border-right: ${TAIL}px solid transparent;
      border-top: ${TAIL}px solid white;
    }
    .founsi-marker-overlay {
      position: absolute;
      top: -6px;
      right: -6px;
      min-width: 18px;
      height: 18px;
      padding: 0 4px;
      border-radius: 9px;
      background: white;
      color: ${fpColors.onSurface};
      font-family: 'Inter_700Bold', system-ui, sans-serif;
      font-size: 12px;
      line-height: 18px;
      text-align: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    .founsi-marker-overlay-lent-out { color: ${fpColors.tertiary}; }
    .founsi-marker-overlay-borrowed { color: ${fpColors.primary}; }
    .founsi-marker-overlay-overdue {
      background: ${fpColors.error};
      color: white;
      font-weight: 900;
    }
    .founsi-marker-count {
      position: absolute;
      bottom: 2px;
      right: 2px;
      min-width: 24px;
      height: 20px;
      padding: 0 6px;
      border-radius: 10px;
      background: white;
      color: ${fpColors.onSurface};
      font-family: 'Inter_700Bold', system-ui, sans-serif;
      font-size: 12px;
      line-height: 20px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    /* Override Leaflet's marker-icon background so our divIcon
       shows through cleanly. */
    .leaflet-marker-icon.founsi-marker-wrapper {
      background: transparent !important;
      border: none !important;
    }
    /* Blue user-location dot — placed by the locate-me button. */
    .founsi-user-dot {
      width: 18px;
      height: 18px;
      border-radius: 9px;
      background: #2196F3;
      border: 3px solid white;
      box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.25),
                  0 2px 6px rgba(0,0,0,0.3);
    }
    /* Memory Map — faded ghost marker at original scan location.
       Half opacity + dashed border to distinguish from the live
       current-location pin. */
    .founsi-scan-ghost {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      overflow: hidden;
      border: 2px dashed rgba(0, 0, 0, 0.45);
      opacity: 0.55;
      background: white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.18);
    }
    .founsi-scan-ghost img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

export const MARKER_SIZE = SIZE;
export const MARKER_TAIL = TAIL;
